class SocialFloating extends HTMLElement {
  constructor() {
    super();

    // Prevent popup on Shopify robot challenge page
    if (window.location.pathname === '/challenge') {
      return;
    }

    this.classes = {
      activeClass: 'is-active',
      closingClass: 'is-closing'
    };

    this.querySelectorAll('[data-social-toggle]').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  onButtonClick(event) {
    event.preventDefault();
    if (!theme.config.mqlSmall) return;

    this.classList.contains(this.classes.activeClass) ? this.close() : this.open();
  }

  open() {
    this.classList.add(this.classes.activeClass);

    // close promo-popup
    const popup = document.querySelector('promo-popup');
    popup && popup.close();
  }

  close() {
    this.classList.add(this.classes.closingClass);

    setTimeout(() => {
      this.classList.remove(this.classes.activeClass);
      this.classList.remove(this.classes.closingClass);
    }, 500);
  }
}
customElements.define('social-floating', SocialFloating);

class PromoPopup extends HTMLElement {
  constructor() {
    super();

    // Prevent popup on Shopify robot challenge page
    if (window.location.pathname === '/challenge') {
      return;
    }

    this.cookieName = 'beyours:promo-popup';

    this.classes = {
      bodyClass: 'promo-popup--open',
      activeClass: 'is-active',
      closingClass: 'is-closing',
      showImage: 'show-image'
    };

    this.popup = this.querySelector('.promo-popup');
    const cookieName = this.popup.getAttribute('data-name');
    if (cookieName) {
      this.cookieName = cookieName; 
    }

    // Open modal if errors or success message exist
    if (this.querySelector('.form__message') && (! this.querySelector('.form-html'))) {      
      this.open();
    }

    this.querySelectorAll('[data-popup-toggle]').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
    
    const cookieVal = this.getCookie(this.cookieName);    

    if (!cookieVal || this.dataset.testMode === 'true') {
      this.init();
    }
  }

  connectedCallback() {
    if (Shopify.designMode) {
      this.onShopifySectionLoad = this.onSectionLoad.bind(this);
      this.onShopifySectionSelect = this.onSectionSelect.bind(this);
      this.onShopifySectionDeselect = this.onSectionDeselect.bind(this);
      document.addEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.addEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.addEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
    }
  }
  disconnectedCallback() {
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.removeEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onShopifySectionDeselect);

      document.body.classList.remove(this.classes.bodyClass);
    }
  }
  onSectionLoad(event) {
    filterShopifyEvent(event, this, () => this.open.bind(this));
  }
  onSectionSelect(event) {
    filterShopifyEvent(event, this, this.open.bind(this));
  }
  onSectionDeselect(event) {
    filterShopifyEvent(event, this, this.close.bind(this));
  }

  init() {
    if (Shopify && Shopify.designMode) {
      return;
    }

    setTimeout(function() {
      this.openWhenIdle();
    }.bind(this), parseInt(this.dataset.delay) * 1000);
  }

  /*
    DLG-01 / CMP-02 - This dialog is configured to open by itself on every page
    after a delay (43 seconds at the time of writing). Opening a modal on top of
    someone who is midway through typing takes their focus and their context with
    no warning, so the timer now waits for a natural pause: it defers while a form
    field has focus or while another dialog is already open, rechecking every five
    seconds. The retry count is capped so a page with a permanently focused field
    does not keep a timer alive indefinitely.
  */
  openWhenIdle(attempt = 0) {
    if (attempt < 12 && this.isUserBusy()) {
      setTimeout(() => this.openWhenIdle(attempt + 1), 5000);
      return;
    }

    this.open();
  }

  isUserBusy() {
    const active = document.activeElement;

    if (
      active &&
      active !== document.body &&
      typeof active.matches === 'function' &&
      active.matches('input:not([type="hidden"]), textarea, select, [contenteditable="true"]')
    ) {
      return true;
    }

    // Body classes the theme's other dialogs set while they are open.
    return [
      'search-modal--open',
      'mini-cart--open',
      'menu-mobile--open',
      'menu-drawer--open',
      'facet-drawer--open',
      'quick-view--open',
      'details-modal--open',
      'pickup-availability--open'
    ].some((openClass) => document.body.classList.contains(openClass));
  }

  onButtonClick(event) {
    event.preventDefault();
    this.popup.classList.contains(this.classes.activeClass) ? this.close() : this.open();
  }

  open() {
    // Idempotent: open() is reachable from the timer, the toggle and design mode.
    if (this.popup.classList.contains(this.classes.activeClass)) return;

    document.body.classList.remove(this.classes.bodyClass);
    this.popup.classList.add(this.classes.activeClass);
    setTimeout(() => {
      this.popup.classList.add(this.classes.showImage);
    }, 600);

    if (this.popup.dataset.position === 'center') {
      setScrollbarWidth();
      document.body.classList.add(this.classes.bodyClass);
    }

    // close social-floating
    const social = document.querySelector('social-floating');
    social && social.close();

    /*
      DLG-01 / DLG-03 - Focus lifecycle.
      WCAG 2.2 SC 2.1.1 Keyboard, 2.1.2 No Keyboard Trap, 2.4.3 Focus Order,
      4.1.2 Name Role Value.

      Previously open() only added CSS classes: focus never entered the dialog,
      nothing contained it, Escape did nothing, and the page behind stayed both
      tabbable and readable by a screen reader's virtual cursor.

      The trap container is `.promo-popup` rather than the inner `.popup-wrapper`
      so the click-to-dismiss overlay stays inside the trap and does not get
      inerted along with the rest of the page.

      Focus is moved on the next frame because `.promo-popup` is display:none until
      `.is-active` is applied, and the shared trap deliberately skips elements that
      have no layout box.
    */
    /*
      The dialog animates in, so the frame after the class lands is not reliably
      the frame in which the close button has a layout box - and focus() on an
      element that is not yet rendered silently does nothing. Retry across a few
      frames until focus is genuinely inside the dialog, then stop. Observed as an
      intermittent "focus did not move into the popup" under load.
    */
    const focusIntoDialog = (attempt = 0) => {
      // close() can win the race if the dialog is dismissed as it opens; trapping
      // a closed dialog would strand focus.
      if (!this.popup.classList.contains(this.classes.activeClass)) return;

      const closeButton = this.popup.querySelector('.popup-close');
      const target = closeButton && closeButton.offsetParent !== null ? closeButton : null;

      if (!target) {
        if (attempt < 20) requestAnimationFrame(() => focusIntoDialog(attempt + 1));
        return;
      }

      trapFocus(this.popup, target, {
        modal: true,
        onEscape: () => this.close()
      });

      if (!this.popup.contains(document.activeElement) && attempt < 20) {
        requestAnimationFrame(() => focusIntoDialog(attempt + 1));
      }
    };

    requestAnimationFrame(() => focusIntoDialog());
  }

  close() {
    if (!this.popup.classList.contains(this.classes.activeClass)) return;

    // Released immediately rather than after the 500ms close animation, so a
    // keyboard user is not left without a focus position while it plays.
    removeTrapFocus();

    this.popup.classList.add(this.classes.closingClass);

    setTimeout(() => {
      this.popup.classList.remove(this.classes.activeClass);
      this.popup.classList.remove(this.classes.closingClass);
      this.popup.classList.remove(this.classes.showImage);

      if (this.popup.dataset.position === 'center') {
        document.body.classList.remove(this.classes.bodyClass);
      }
    }, 500);

    // Remove a cookie in case it was set in test mode
    if (this.dataset.testMode === 'true') {
      this.removeCookie(this.cookieName);
      return;
    }

    this.setCookie(this.cookieName, this.dataset.expiry);
  }

  getCookie(name) { 
    const value = localStorage.getItem(name); 
    if (! value) {
      return null; 
    }
    const now = new Date(); 
    if (now.getTime() > (value * 1)) {
      localStorage.removeItem(name);
      return null;
    }

    return true;

    // const cookie = document.cookie; console.log(cookie);
    // const match = cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`); console.log(match);
    // return match ? match[2] : null;
  }

  setCookie(name, expiry) { 
    const now = new Date();
    const time = now.getTime() + expiry * 24 * 60 * 60 * 1000;
    localStorage.setItem(name, time);
    //document.cookie = `${name}=true; max-age=${(expiry * 24 * 60 * 60)};`;
  }

  removeCookie(name) { 
    localStorage.removeItem(name);
    //document.cookie = `${name}=; max-age=0;`;
  }
}
customElements.define('promo-popup', PromoPopup);
